import type { QueryClient } from '@tanstack/react-query'
import type {
  BatteryPlug,
  CallReason,
  NetworkType,
  OwnerKind,
  Phone,
  PhoneMetrics,
  PhoneStatus,
  SimState,
} from '../types'

// --- API → domain mapping ---

export interface ApiPhone {
  id: string
  name: string
  sim: string
  api_token?: string
  /**
   * null for orphan phones — admin pool before access-key attachment.
   * Previously typed as `string` which was wrong since the orphan migration.
   */
  user_id: string | null
  is_active: boolean
  status: string
  /** Admin responses only — derived from phone.user.role on the backend. */
  owner_kind?: OwnerKind
  /** Admin responses only — operator access-key label or fallback. */
  owner_label?: string | null
  // Optional for backward-compat with backends deployed before
  // call-reason classification — mapper below defaults to null/0.
  last_disconnect_reason?: CallReason | null
  consecutive_failed_calls?: number
  // Telemetry — older backends omit; mapper defaults to nulls.
  battery_pct?: number | null
  battery_charging?: boolean | null
  battery_plugged?: string | null
  sim_state?: string | null
  carrier_name?: string | null
  network_type?: string | null
  signal_dbm?: number | null
  signal_bars?: number | null
  data_roaming?: boolean | null
  free_storage_mb?: number | null
  audio_mode?: string | null
  uptime_seconds?: number | null
  // Per-permission granted/denied map; older agents/backends omit it.
  runtime_permissions?: Record<string, boolean> | null
  // Agent's APK versionName; older agents/backends omit it.
  app_version?: string | null
  // Android OS release version + device model; older agents/backends omit them.
  os_version?: string | null
  phone_model?: string | null
  metrics_updated_at?: string | null
}

function toPhoneMetrics(p: ApiPhone): PhoneMetrics {
  return {
    batteryPct: p.battery_pct ?? null,
    batteryCharging: p.battery_charging ?? null,
    batteryPlugged: (p.battery_plugged ?? null) as BatteryPlug | null,
    simState: (p.sim_state ?? null) as SimState | null,
    carrierName: p.carrier_name ?? null,
    networkType: (p.network_type ?? null) as NetworkType | null,
    signalDbm: p.signal_dbm ?? null,
    signalBars: p.signal_bars ?? null,
    dataRoaming: p.data_roaming ?? null,
    freeStorageMb: p.free_storage_mb ?? null,
    audioMode: p.audio_mode ?? null,
    uptimeSeconds: p.uptime_seconds ?? null,
    runtimePermissions: p.runtime_permissions ?? null,
    appVersion: p.app_version ?? null,
    osVersion: p.os_version ?? null,
    phoneModel: p.phone_model ?? null,
    metricsUpdatedAt: p.metrics_updated_at ?? null,
  }
}

export function apiPhoneToPhone(p: ApiPhone): Phone {
  return {
    id: p.id,
    name: p.name,
    sim: p.sim,
    status: p.status === 'online' ? 'online' : 'offline',
    apiToken: p.api_token,
    userId: p.user_id,
    isActive: p.is_active,
    ownerKind: p.owner_kind,
    ownerLabel: p.owner_label ?? null,
    lastDisconnectReason: p.last_disconnect_reason ?? null,
    consecutiveFailedCalls: p.consecutive_failed_calls ?? 0,
    metrics: toPhoneMetrics(p),
  }
}

/**
 * Merge API response into cache, preserving real-time fields (status, currentCall)
 * that WebSocket maintains. API is source of truth for static fields (name, sim,
 * isActive, apiToken). WebSocket is source of truth for real-time fields.
 *
 * On first load (no cache): API data used as-is (status from DB).
 * On refetch (cache exists): API updates static fields, real-time fields kept.
 */
export function mergeWithCached(fresh: Phone, cached: Phone | undefined): Phone {
  if (!cached) return fresh
  return {
    ...fresh,
    status: cached.status,
    currentCall: cached.currentCall,
  }
}

// --- Relay error messages ---

export const RELAY_ERRORS: Record<string, string> = {
  not_authorized: 'Not authorized to perform this action',
  agent_offline: 'Phone agent is offline',
  phone_busy: 'Phone is busy',
  already_in_call: 'Already in a call',
  blocked: 'This number is blocked',
  agent_timeout: 'Phone agent did not respond',
}

// --- Cache writers (WebSocket → React Query cache) ---

export function updatePhoneInCache(qc: QueryClient, phoneId: string, updates: Partial<Phone>): void {
  qc.setQueryData<Phone[]>(['phones'], (old) =>
    old?.map(p => p.id === phoneId ? { ...p, ...updates } : p)
  )
  qc.setQueryData<Phone>(['phones', phoneId], (old) =>
    old ? { ...old, ...updates } : old
  )
}

export const STATUS_MAP: Record<string, PhoneStatus> = {
  online: 'online',
  offline: 'offline',
  in_call: 'in-call',
  reconnecting: 'reconnecting',
}

export function setPhoneStatus(qc: QueryClient, phoneId: string, status: string): void {
  const phoneStatus: PhoneStatus = STATUS_MAP[status] ?? 'offline'
  updatePhoneInCache(qc, phoneId, { status: phoneStatus })
}

export function makeCall(
  callId: string,
  number: string,
  state: 'dialing' | 'ringing' | 'active',
  startedAt?: string,
): Partial<Phone> {
  return {
    currentCall: { callId, number, state, startedAt: startedAt ?? new Date().toISOString() },
    status: 'in-call',
  }
}

/**
 * Returns the startedAt of the call already cached for this phone when it is
 * the same call_id AND already in the active state. Keeps the on-screen
 * elapsed-time counter stable: the relay re-sends call_state(active) for the
 * in-progress call on every browser WS reconnect, and a fresh startedAt would
 * reset the timer to 0:00 mid-call. Reads both caches because the list can be
 * GC'd while PhoneCallView holds the per-phone entry (see disconnected branch).
 */
function preservedActiveStartedAt(qc: QueryClient, phoneId: string, callId: string): string | undefined {
  const existing =
    qc.getQueryData<Phone[]>(['phones'])?.find(p => p.id === phoneId)?.currentCall
    ?? qc.getQueryData<Phone>(['phones', phoneId])?.currentCall
  return existing?.callId === callId && existing.state === 'active'
    ? existing.startedAt
    : undefined
}

// --- #7 Layer 3: recent-disconnected TTL map ---
//
// Blocks stale currentCall updates that arrive milliseconds after a
// `call_state:disconnected` for the same call_id. Relay guards the main
// race (Layer 1 re-checks Calls.Get before OnBrowserSnapshot), but a
// microsecond window between Calls.Get and SendMsg still exists and
// can't be closed without breaking hub.mu ordering. The TTL map catches
// the leftovers on the client.
//
// Keyed on call_id. Empty-string ids (OnBrowserCallClear shape) are
// intentionally ignored — they'd poison the map for every future
// empty-id message.
const RECENT_DISCONNECTED_TTL_MS = 5000
const recentlyDisconnected = new Map<string, number>()

function gcRecentlyDisconnected(now: number): void {
  const cutoff = now - RECENT_DISCONNECTED_TTL_MS
  for (const [callId, ts] of recentlyDisconnected) {
    if (ts <= cutoff) recentlyDisconnected.delete(callId)
  }
}

function isRecentlyDisconnected(callId: string, now: number): boolean {
  if (!callId) return false
  const ts = recentlyDisconnected.get(callId)
  if (ts === undefined) return false
  return now - ts < RECENT_DISCONNECTED_TTL_MS
}

/** Test-only helper — clears the TTL map so unit tests aren't coupled by
 * shared module state. Not exported via the public api/ index. */
export function __resetRecentlyDisconnectedForTests(): void {
  recentlyDisconnected.clear()
}

export function handleCallState(qc: QueryClient, msg: {
  call_id: string; state: string; phone_id?: string; number?: string; reason?: string
}): void {
  const now = Date.now()
  gcRecentlyDisconnected(now)

  // Find phone by phone_id (preferred) or by matching call_id in cached phones
  const phones = qc.getQueryData<Phone[]>(['phones'])
  const phoneId = msg.phone_id
    || phones?.find(p => p.currentCall?.callId === msg.call_id)?.id
  if (!phoneId) return

  // Block stale currentCall-setting messages for a call_id that was
  // just disconnected. Covers every branch below that calls makeCall.
  if (msg.state !== 'disconnected' && isRecentlyDisconnected(msg.call_id, now)) {
    console.warn('relay-cache: ignoring call_state after recent disconnected', msg.state, msg.call_id)
    return
  }

  if (msg.state === 'disconnected') {
    // Remember this call_id so any racing snapshot-born currentCall
    // update for the same id is suppressed (Layer 3). Skip empty ids
    // (OnBrowserCallClear shape).
    if (msg.call_id) recentlyDisconnected.set(msg.call_id, now)

    // Set status to 'online' only if still 'in-call'. If phone_status already
    // changed it to 'offline'/'reconnecting' (dead agent), don't overwrite.
    // Check both list cache AND individual cache — list may be GC'd while
    // PhoneCallView (active dialer on UserCallPage) holds a per-phone entry.
    const current = phones?.find(p => p.id === phoneId)?.status
      ?? qc.getQueryData<Phone>(['phones', phoneId])?.status
    const status = current === 'in-call' ? 'online' as const : undefined
    updatePhoneInCache(qc, phoneId, { currentCall: undefined, ...(status && { status }) })
    // Invalidate call history so it picks up the new call_ended record
    qc.invalidateQueries({ queryKey: ['calls'] })
    qc.invalidateQueries({ queryKey: ['callStats'] })
  } else if (msg.state === 'active') {
    const startedAt = preservedActiveStartedAt(qc, phoneId, msg.call_id)
    updatePhoneInCache(qc, phoneId, makeCall(msg.call_id, msg.number || '', 'active', startedAt))
  } else if (msg.state === 'initiating' || msg.state === 'dialing') {
    updatePhoneInCache(qc, phoneId, makeCall(msg.call_id, msg.number || '', 'dialing'))
  } else if (msg.state === 'ringing') {
    updatePhoneInCache(qc, phoneId, makeCall(msg.call_id, msg.number || '', 'ringing'))
  }
}

export function handleIncomingCall(qc: QueryClient, msg: {
  call_id: string; phone_id: string; number: string
}): void {
  const now = Date.now()
  gcRecentlyDisconnected(now)
  if (isRecentlyDisconnected(msg.call_id, now)) {
    console.warn('relay-cache: ignoring incoming_call after recent disconnected', msg.call_id)
    return
  }
  updatePhoneInCache(qc, msg.phone_id, makeCall(msg.call_id, msg.number, 'ringing'))
}

export function resetAllPhonesOffline(qc: QueryClient): void {
  const phones = qc.getQueryData<Phone[]>(['phones'])
  qc.setQueryData<Phone[]>(['phones'], (old) =>
    old?.map(p => ({ ...p, status: 'offline' as const, currentCall: undefined }))
  )
  // Also update individual phone caches — PhoneCallView reads ['phones', id]
  phones?.forEach(p => {
    qc.setQueryData<Phone>(['phones', p.id], (old) =>
      old ? { ...old, status: 'offline' as const, currentCall: undefined } : old
    )
  })
}
